import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Accordion } from "react-bootstrap";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import ApiRequest from "../../helper/ApiRequest";
import CustomSelect from "../../component/CustomSelect";
import CustomImageUpload from "../../component/CustomImageUpload";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";

const saveFirstTabEndpoint = ApiEndpoints.disclosure.SaveFirstTab;
const saveTabBase = ApiEndpoints.disclosure.SaveTab;
const updateEndpoint = ApiEndpoints.disclosure.Update;
const branchListEndpoint = ApiEndpoints.branch.branchList;

const tabConfigs = [
  { key: "first", label: "Tab 1", isTextTab: true },
  { key: "second", label: "Tab 2", isTextTab: false },
  { key: "third", label: "Tab 3", isTextTab: false },
  { key: "fourth", label: "Tab 4", isTableTab: true },
  { key: "fifth", label: "Tab 5", isTableTab: true },
  { key: "sixth", label: "Tab 6", isTextTab: true },
  { key: "seventh", label: "Tab 7", isTextTab: true },
];

const emptyRow = (tab) => {

  // TABLE TAB
  if (tab.isTableTab) {
    return {
      year: "",
      registered_student: "",
      student_passed: "",
      percentage: "",
    };
  }

  // SIXTH TAB
  if (tab.key === "sixth") {
    return {
      information: "",
      details: "",
      document: null,
    };
  }

  // TEXT TAB
  if (tab.isTextTab) {
    return {
      information: "",
      details: "",
    };
  }

  // FILE TAB
  return {
    information: "",
    document: null,
  };
};

export default function DisclosureAdd() {

  const navigate = useNavigate();

  const [branches, setBranches] = useState([]);
  const [recordId, setRecordId] = useState(null);
  const [saving, setSaving] = useState(null);
  const [savedTabs, setSavedTabs] = useState(new Set());

  const [formData, setFormData] = useState({
    branch: [],

    main_disclosure: null,

    first_tab_title: "",
    first_tab_info: [],

    second_tab_title: "",
    second_tab_info: [],

    third_tab_title: "",
    third_tab_info: [],

    fourth_tab_title: "",
    fourth_tab_info: [],

    fifth_tab_title: "",
    fifth_tab_info: [],

    sixth_tab_title: "",
    sixth_tab_info: [],

    seventh_tab_title: "",
    seventh_tab_info: [],
  });

  useEffect(() => {
    ApiRequest("GET", branchListEndpoint)
      .then((res) => setBranches(res?.branch || []))
      .catch(console.error);
  }, []);

  const handleBranchChange = (e) => {

    const ids = e.target.value
      ? e.target.value.split(",").filter(Boolean)
      : [];

    setFormData((prev) => ({
      ...prev,
      branch: ids,
    }));
  };

  const handleTitleChange = (tabKey, value) => {

    setFormData((prev) => ({
      ...prev,
      [`${tabKey}_tab_title`]: value,
    }));
  };

  const handleRowChange = (tabKey, index, field, value) => {

    setFormData((prev) => {

      const key = `${tabKey}_tab_info`;

      const updated = [...prev[key]];

      updated[index] = {
        ...updated[index],
        [field]: value,
      };

      return {
        ...prev,
        [key]: updated,
      };
    });
  };

  const addRow = (tab) => {

    setFormData((prev) => {

      const key = `${tab.key}_tab_info`;

      return {
        ...prev,
        [key]: [...prev[key], emptyRow(tab)],
      };
    });
  };

  const removeRow = (tabKey, index) => {

    setFormData((prev) => {

      const key = `${tabKey}_tab_info`;

      return {
        ...prev,
        [key]: prev[key].filter((_, i) => i !== index),
      };
    });
  };

  const buildTabFormData = (
    tabKey,
    isTextTab,
    isTableTab
  ) => {

    const fd = new FormData();

    fd.append(
      "branch",
      JSON.stringify(formData.branch)
    );

    fd.append(
      `${tabKey}_tab_title`,
      formData[`${tabKey}_tab_title`]
    );


    if (
      tabKey === "first" &&
      formData.main_disclosure instanceof File
    ) {

      fd.append(
        "main_disclosure",
        formData.main_disclosure
      );
    }

    // TABLE TAB
    if (isTableTab) {

      fd.append(
        `${tabKey}_tab_info`,
        JSON.stringify(
          formData[`${tabKey}_tab_info`].map(
            ({
              year,
              registered_student,
              student_passed,
              percentage,
            }) => ({
              year,
              registered_student,
              student_passed,
              percentage,
            })
          )
        )
      );

      return fd;
    }

    // TEXT TAB
    if (isTextTab) {

      // SIXTH TAB WITH OPTIONAL FILE
      // SIXTH TAB WITH OPTIONAL FILE
      if (tabKey === "sixth") {

        const sixthData = formData[`${tabKey}_tab_info`].map(
          ({ information, details }) => ({
            information,
            details,
            document: null,
          })
        );

        fd.append(
          `${tabKey}_tab_info`,
          JSON.stringify(sixthData)
        );

        formData[`${tabKey}_tab_info`].forEach((row, index) => {

          if (row.document instanceof File) {

            fd.append(
              `${tabKey}_tab_info_document`,
              row.document
            );

            fd.append(
              `${tabKey}_tab_info_document_index`,
              index.toString()
            );

          }

        });

      } else {

        fd.append(
          `${tabKey}_tab_info`,
          JSON.stringify(
            formData[`${tabKey}_tab_info`].map(
              ({ information, details }) => ({
                information,
                details,
              })
            )
          )
        );

      }

    } else {

      // FILE TAB

      fd.append(
        `${tabKey}_tab_info`,
        JSON.stringify(
          formData[`${tabKey}_tab_info`].map(
            ({ information }) => ({
              information,
            })
          )
        )
      );

      formData[`${tabKey}_tab_info`].forEach((row) => {

        if (row.document instanceof File) {

          fd.append(
            `${tabKey}_tab_info_document`,
            row.document
          );

        }

      });

    }

    return fd;
  };

  const handleSave = async (
    tabKey,
    isTextTab,
    isTableTab
  ) => {

    if (!formData.branch.length) {
      toast.error("Please select at least one branch.");
      return;
    }

    if (!formData[`${tabKey}_tab_title`].trim()) {
      toast.error("Tab title is required.");
      return;
    }

    const infoRows = formData[`${tabKey}_tab_info`];

    if (!infoRows.length) {
      toast.error("At least one row is required.");
      return;
    }

    for (let i = 0; i < infoRows.length; i++) {

      const row = infoRows[i];

      // TABLE TAB
      if (isTableTab) {

        if (!row.year?.trim()) {
          toast.error(`Row ${i + 1}: Year is required.`);
          return;
        }

        if (!row.registered_student?.trim()) {
          toast.error(`Row ${i + 1}: Registered Student is required.`);
          return;
        }

        if (!row.student_passed?.trim()) {
          toast.error(`Row ${i + 1}: Student Passed is required.`);
          return;
        }

        if (!row.percentage?.trim()) {
          toast.error(`Row ${i + 1}: Percentage is required.`);
          return;
        }

        continue;
      }

      // TEXT + FILE TAB

      if (!row.information?.trim()) {
        toast.error(`Row ${i + 1}: Information field is required.`);
        return;
      }

      // TEXT TAB
      if (isTextTab) {

        if (!row.details?.trim()) {
          toast.error(`Row ${i + 1}: Details field is required.`);
          return;
        }

      } else {

        // FILE TAB

        if (!(row.document instanceof File)) {
          toast.error(`Row ${i + 1}: Document is required.`);
          return;
        }
      }
    }

    if (tabKey !== "first" && !recordId) {
      toast.error("Please save Tab 1 first.");
      return;
    }

    if (
      tabKey === "first" &&
      !(formData.main_disclosure instanceof File)
    ) {
      toast.error("Disclosure document is required.");
      return;
    }

    setSaving(tabKey);

    try {

      const fd = buildTabFormData(
        tabKey,
        isTextTab,
        isTableTab
      );

      if (tabKey === "first") {

        const response = await ApiRequest(
          "POST",
          saveFirstTabEndpoint,
          fd
        );

        const newId =
          response?.data?._id || response?._id;

        if (newId) {
          setRecordId(newId);
        }

        toast.success(
          response?.message || "Saved successfully"
        );

      } else {

        const tabEndpoint =
          `${saveTabBase}${recordId}/tab/${tabKey}`;

        await ApiRequest(
          "POST",
          tabEndpoint,
          fd
        );

        const titleFd = new FormData();

        titleFd.append(
          `${tabKey}_tab_title`,
          formData[`${tabKey}_tab_title`]
        );

        const response = await ApiRequest(
          "POST",
          updateEndpoint + recordId,
          titleFd
        );

        toast.success(
          response?.message || "Saved successfully"
        );
      }

      const updatedSaved =
        new Set(savedTabs).add(tabKey);

      setSavedTabs(updatedSaved);

      const allTabKeys =
        tabConfigs.map((t) => t.key);

      if (
        allTabKeys.every((k) =>
          updatedSaved.has(k)
        )
      ) {
        navigate("/disclosure");
      }

    } catch (error) {

      console.error("Save error:", error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save. Please try again.";

      if (
        errorMessage.toLowerCase().includes("already exists") ||
        errorMessage.toLowerCase().includes("already assigned")
      ) {

        const duplicateBranches =
          error?.response?.data?.duplicate_branches || [];

        if (duplicateBranches.length > 0) {

          const branchNames =
            duplicateBranches
              .map((b) => b.name)
              .join(", ");

          toast.error(
            `Selected branch already exists: ${branchNames}`
          );

        } else {

          toast.error(
            "Selected branch already exists."
          );
        }

      } else {

        toast.error(errorMessage);
      }

    } finally {

      setSaving(null);
    }
  };

  const pagename = {
    name: "Mandatory Disclosure",
    user: "Admin",
    innerpage: "Mandatory Disclosure / Add",
  };

  return (
    <>
      <AdminHeader pagename={pagename} />

      <div className="add-content-page">

        {/* Branch */}
        <div className="add-content-wrap">
          <div className="row">

            <div className="col-lg-6">
              <div className="add-page-inputs">
                <div className="label-wrap-add">
                  <label>
                    Select Multiple Branch
                    <small>*</small>
                  </label>
                </div>

                <CustomSelect
                  name="branch"
                  options={branches}
                  placeholder="Select Branch"
                  multiple={true}
                  defaultValue={formData.branch}
                  onChange={handleBranchChange}
                />
              </div>
            </div>

            <div className="col-lg-6">
              <div className="add-page-inputs">
                <div className="label-wrap-add">
                  <label>
                    Disclosure Document (PDF/Word)
                    <small>*</small>
                  </label>
                </div>

                <CustomImageUpload
                  name="main_disclosure"
                  acceptedFormats={[
                    ".pdf",
                    ".doc",
                    ".docx",
                  ]}
                  onImageChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      main_disclosure:
                        e.target.files[0] || null,
                    }))
                  }
                  imagePreview={
                    formData.main_disclosure instanceof File
                      ? formData.main_disclosure.name
                      : formData.main_disclosure
                        ? formData.main_disclosure
                          .split("/")
                          .pop()
                        : null
                  }
                />
              </div>
            </div>

          </div>
        </div>

        {/* Tabs */}
        <div className="add-content-wrap">
          <div className="row">

            <div className="col-lg-12">

              <div className="home-slove-accord">

                <Accordion defaultActiveKey="0">

                  {tabConfigs.map((tab, idx) => (

                    <Accordion.Item
                      eventKey={idx.toString()}
                      key={tab.key}
                    >

                      <Accordion.Header>
                        {formData[`${tab.key}_tab_title`] || tab.label}
                      </Accordion.Header>

                      <Accordion.Body>

                        {/* Title */}
                        <div className="row">

                          <div className="col-lg-12">

                            <div className="add-page-inputs">

                              <div className="label-wrap-add">
                                <label>
                                  Tab Title
                                  <small>*</small>
                                </label>
                              </div>

                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter tab title"
                                value={formData[`${tab.key}_tab_title`]}
                                onChange={(e) =>
                                  handleTitleChange(
                                    tab.key,
                                    e.target.value
                                  )
                                }
                              />

                            </div>

                          </div>

                        </div>

                        {/* Rows */}
                        {formData[`${tab.key}_tab_info`].map((row, i) => (

                          <div
                            className="row align-items-end"
                            key={i}
                          >

                            {/* TABLE TAB */}
                            {tab.isTableTab ? (
                              <>

                                <div className="col-lg-2">
                                  <div className="add-page-inputs">

                                    <div className="label-wrap-add">
                                      <label>
                                        Year
                                        <small>*</small>
                                      </label>
                                    </div>

                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Enter Year"
                                      value={row.year}
                                      onChange={(e) =>
                                        handleRowChange(
                                          tab.key,
                                          i,
                                          "year",
                                          e.target.value
                                        )
                                      }
                                    />

                                  </div>
                                </div>

                                <div className="col-lg-3">
                                  <div className="add-page-inputs">

                                    <div className="label-wrap-add">
                                      <label>
                                        Registered Student
                                        <small>*</small>
                                      </label>
                                    </div>

                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Enter Registered Student"
                                      value={row.registered_student}
                                      onChange={(e) =>
                                        handleRowChange(
                                          tab.key,
                                          i,
                                          "registered_student",
                                          e.target.value
                                        )
                                      }
                                    />

                                  </div>
                                </div>

                                <div className="col-lg-3">
                                  <div className="add-page-inputs">

                                    <div className="label-wrap-add">
                                      <label>
                                        Student Passed
                                        <small>*</small>
                                      </label>
                                    </div>

                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Enter Student Passed"
                                      value={row.student_passed}
                                      onChange={(e) =>
                                        handleRowChange(
                                          tab.key,
                                          i,
                                          "student_passed",
                                          e.target.value
                                        )
                                      }
                                    />

                                  </div>
                                </div>

                                <div className="col-lg-2">
                                  <div className="add-page-inputs">

                                    <div className="label-wrap-add">
                                      <label>
                                        Percentage
                                        <small>*</small>
                                      </label>
                                    </div>

                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Enter Percentage"
                                      value={row.percentage}
                                      onChange={(e) =>
                                        handleRowChange(
                                          tab.key,
                                          i,
                                          "percentage",
                                          e.target.value
                                        )
                                      }
                                    />

                                  </div>
                                </div>

                              </>
                            ) : (
                              <>
                                {/* INFORMATION */}
                                <div className={tab.key === "sixth" ? "col-lg-3" : "col-lg-4"}>

                                  <div className="add-page-inputs">

                                    <div className="label-wrap-add">
                                      <label>
                                        Information
                                        <small>*</small>
                                      </label>
                                    </div>

                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Enter information"
                                      value={row.information}
                                      onChange={(e) =>
                                        handleRowChange(
                                          tab.key,
                                          i,
                                          "information",
                                          e.target.value
                                        )
                                      }
                                    />

                                  </div>

                                </div>

                                {/* TEXT TAB */}
                                {tab.isTextTab ? (

                                  <>
                                    <div className="col-lg-3">

                                      <div className="add-page-inputs">

                                        <div className="label-wrap-add">
                                          <label>
                                            Details
                                            <small>*</small>
                                          </label>
                                        </div>

                                        <input
                                          type="text"
                                          className="form-control"
                                          placeholder="Enter details"
                                          value={row.details}
                                          onChange={(e) =>
                                            handleRowChange(
                                              tab.key,
                                              i,
                                              "details",
                                              e.target.value
                                            )
                                          }
                                        />

                                      </div>

                                    </div>

                                    {/* ONLY 6TH TAB */}
                                    {tab.key === "sixth" && (

                                      <div className="col-lg-4">

                                        <div className="add-page-inputs input-add-image customimage-gap">

                                          <div className="label-wrap-add">
                                            <label>
                                              Document (Optional)
                                            </label>
                                          </div>

                                          <CustomImageUpload
                                            name="document"
                                            acceptedFormats={[
                                              ".pdf",
                                              ".doc",
                                              ".docx",
                                            ]}
                                            onImageChange={(e) =>
                                              handleRowChange(
                                                tab.key,
                                                i,
                                                "document",
                                                e.target.files[0] || null
                                              )
                                            }
                                            imagePreview={null}
                                          />

                                        </div>

                                      </div>

                                    )}
                                  </>

                                ) : (

                                  <div className="col-lg-5">

                                    <div className="add-page-inputs input-add-image customimage-gap">

                                      <div className="label-wrap-add">
                                        <label>
                                          Document (PDF/Word)
                                          <small>*</small>
                                        </label>
                                      </div>

                                      <CustomImageUpload
                                        name="document"
                                        acceptedFormats={[
                                          ".pdf",
                                          ".doc",
                                          ".docx",
                                        ]}
                                        onImageChange={(e) =>
                                          handleRowChange(
                                            tab.key,
                                            i,
                                            "document",
                                            e.target.files[0] || null
                                          )
                                        }
                                        imagePreview={null}
                                      />

                                    </div>
                                  </div>

                                )}

                              </>
                            )}

                            {/* DELETE */}
                            <div className="col-lg-2">

                              <div className="add-page-inputs">

                                <button
                                  type="button"
                                  className="coustom-delete"
                                  onClick={() =>
                                    removeRow(tab.key, i)
                                  }
                                >
                                  <img
                                    src="/assets/admin-delete.webp"
                                    alt="remove row"
                                    className="img-responsive"
                                  />
                                </button>

                              </div>

                            </div>

                          </div>

                        ))}

                        {/* BUTTONS */}
                        <div className="row">

                          <div className="col-lg-12 mt-2">

                            <div
                              className="addmore-images-client"
                              onClick={() => addRow(tab)}
                            >
                              Add Row +
                            </div>

                          </div>

                          <div className="col-lg-12">

                            <div className="btn-group-home-solve">

                              <button
                                type="button"
                                className="default-btn"
                                onClick={() =>
                                  handleSave(
                                    tab.key,
                                    tab.isTextTab,
                                    tab.isTableTab
                                  )
                                }
                                disabled={saving === tab.key}
                              >
                                {saving === tab.key
                                  ? "Saving..."
                                  : "Save"}

                                <span></span>
                              </button>

                            </div>

                          </div>

                        </div>

                      </Accordion.Body>

                    </Accordion.Item>

                  ))}

                </Accordion>

              </div>

            </div>

          </div>
        </div>

      </div>
    </>
  );
}